import React, { useState } from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';
import api from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Donate = () => {
  // ── State ──
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState('');

  // ── Constants ──
  const fixedAmounts = [1000, 3000, 5000, 8000];
  const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_TK2vhuQT75BPi2';

  // ── Handlers ──
  const handleAmountSelect = (value) => {
    setAmount(value);
    setCustomAmount('');
    setError('');
  };

  const handleCustomAmountChange = (e) => {
    const val = e.target.value;
    setCustomAmount(val);
    if (val) {
      setAmount(parseInt(val, 10));
    }
    setError('');
  };

  // ── Load Razorpay script ──
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // ── Handle Donation ──
  const handleDonate = async (e) => {
    e.preventDefault();
    setError('');

    if (!name || !email || !phone) {
      //setError('Please fill in your name, email, and phone.');
      toast.error('Please fill in your name, email, and phone.');
      return;
    }
    if (!amount || amount < 500) {
      //setError('Please select or enter a valid donation amount.');
      toast.error('Please select or enter a valid donation amount.');
      return;
    }

    setLoading(true);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Razorpay SDK failed to load. Please check your internet connection.');
        setLoading(false);
        return;
      }

      const response = await api.post('/create-order', {
        amount: amount * 100,
        currency: 'INR',
        donor_name: name,
        donor_email: email,
        donor_phone: phone,
        donor_address: address,
        donor_state: state,
        donor_country: country,
        donor_category: category,
        notes: 'Donation via website',
      });

      const data = response.data;

      if (!data.order_id) {
        throw new Error('Failed to create order. Please try again.');
      }

      const options = {
        key: RAZORPAY_KEY_ID,
        amount: amount * 100,
        currency: 'INR',
        name: 'Venkatadri Seva Trust',
        description: `Donation of ₹${amount} for charity`,
        image: "assets/img/logo/favicon.png",
        order_id: data.order_id,
        prefill: {
          name: name,
          email: email,
          contact: phone,
        },
        notes: {
          address: address,
          state: state,
          country: country,
          category: category,
        },
        theme: {
          color: '#d97706',
        },
        handler: async function (response) {
          try {
            const verifyResponse = await api.post('/verify-payment', {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              setPaymentDetails({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: amount,
                name: name,
                email: email,
                address: address,
                state: state,
                country: country,
                category: category,
              });
              setShowSuccess(true);
              setLoading(false);
              setAmount(1000);
              setCustomAmount('');
              setPhone('');
              setAddress('');
              setState('');
              setCountry('');
              setCategory('');
            } else {
              setError('Payment verification failed. Please contact support.');
              setLoading(false);
            }
          } catch (error) {
            console.error('Verification error:', error);
            setError('Payment verification failed. Please contact support.');
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            setError('Payment was cancelled. You can try again.');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Donation error:', error);
      setError(error.message || 'Something went wrong. Please try again later.');
      setLoading(false);
    }
  };

  // ── Close success modal ──
  const closeSuccessModal = () => {
    setShowSuccess(false);
    setPaymentDetails(null);
    setName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setState('');
    setCountry('');
    setCategory('');
  };

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="donate-hero relative text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-hand-holding-heart mr-2"></i> Support Our Mission
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Make a Donation
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Your contribution brings hope, dignity, and happiness to the underprivileged
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== DONATION FORM ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Donate Now</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Every rupee you donate goes directly to our charitable programmes. Your support is tax-deductible under 80G.
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <i className="fas fa-exclamation-circle mr-2"></i> {error}
              </div>
            )}

            <form onSubmit={handleDonate} className="space-y-6">
              {/* Donor Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Full Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your full name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-1">Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Address Fields */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Address *</label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street, area, landmark"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">State / Province *</label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="Karnataka"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required

                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Country *</label>
                  <input
                    type="text"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="India"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              {/* Category Dropdown */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Donation Category *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary" required
                >
                  <option value="">Select a category</option>
                  <option value="Education for Poor Students">Education for Poor Students</option>
                  <option value="Child Welfare">Child Welfare</option>
                  <option value="Health & Medical Assistance">Health & Medical Assistance</option>
                  <option value="Care for Senior Citizens">Care for Senior Citizens</option>

                  <option value="Support for Persons with Disabilities">Support for Persons with Disabilities</option>


                  <option value="Environment & Nature Conservation">Environment & Nature Conservation</option>
                  <option value="Community Service">Community Service</option>
                </select>
              </div>

              {/* Amount Selection */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Select Donation Amount (₹)</label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {fixedAmounts.map((val) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => handleAmountSelect(val)}
                      className={`px-4 py-2 rounded-lg font-medium transition-smooth ${amount === val && !customAmount
                        ? 'bg-primary text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                    >
                      ₹{val}
                    </button>
                  ))}
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">Other:</span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                      placeholder="Enter amount"
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      min="1"
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">* Minimum donation: ₹500</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold text-white transition-smooth ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-dark'
                  }`}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i> Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-hand-holding-heart mr-2"></i> Donate ₹{amount || 0} Now
                  </>
                )}
              </button>
            </form>

            <p className="text-gray-500 text-xs text-center mt-4">
              <i className="fas fa-lock mr-1"></i> Your payment is secured with Razorpay. We never store your card details.
            </p>
            <p className="text-gray-500 text-xs text-center mt-1">
              All donations are eligible for 80G tax exemption. Receipts will be sent to your email.
            </p>
          </div>
        </div>
      </section>

      {/* ========== IMPACT SECTION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Your Donation Changes Lives</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <span className="text-4xl block">🎓</span>
                <p className="mt-2 font-semibold">Educate a Child</p>
                <p className="text-sm text-yellow-100/80">₹12,000 sponsors a child's annual education.</p>
              </div>
              <div>
                <span className="text-4xl block">🏥</span>
                <p className="mt-2 font-semibold">Medical Aid</p>
                <p className="text-sm text-yellow-100/80">₹5,000 can fund a life-saving surgery.</p>
              </div>
              <div>
                <span className="text-4xl block">👵</span>
                <p className="mt-2 font-semibold">Senior Care</p>
                <p className="text-sm text-yellow-100/80">₹1,000 provides monthly essentials for an elder.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SUCCESS MODAL ========== */}
      {showSuccess && paymentDetails && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 relative animate-fade-in">
            <button
              onClick={closeSuccessModal}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
            >
              <i className="fas fa-times text-xl"></i>
            </button>

            <div className="text-center">
              <div className="text-6xl text-green-500 mb-4">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Thank You for Your Donation!</h3>
              <p className="text-gray-600 mb-4">
                Your generous contribution of <strong>₹{paymentDetails.amount}</strong> will help us continue our mission.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-left text-sm space-y-2">
                <p><span className="font-semibold">Payment ID:</span> {paymentDetails.paymentId}</p>
                <p><span className="font-semibold">Order ID:</span> {paymentDetails.orderId}</p>
                <p><span className="font-semibold">Donor:</span> {paymentDetails.name}</p>
                <p><span className="font-semibold">Email:</span> {paymentDetails.email}</p>
                {paymentDetails.address && <p><span className="font-semibold">Address:</span> {paymentDetails.address}</p>}
                {paymentDetails.state && <p><span className="font-semibold">State:</span> {paymentDetails.state}</p>}
                {paymentDetails.country && <p><span className="font-semibold">Country:</span> {paymentDetails.country}</p>}
                {paymentDetails.category && <p><span className="font-semibold">Category:</span> {paymentDetails.category}</p>}
              </div>
              <p className="text-xs text-gray-500 mt-4">
                A receipt has been sent to your email. Please allow up to 24 hours for processing.
              </p>
              <button
                onClick={closeSuccessModal}
                className="mt-6 w-full bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primary-dark transition"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />

      <Footer />
    </>
  );
};

export default Donate;